import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { alignVariants, toneVariants, truncateVariants } from "./shared";

const labelVariants = cva(
  "font-(family-name:--font-family-label) font-semibold",
  {
    variants: {
      size: {
        lg: [
          "text-(length:--font-size-label-lg)",
          "leading-(--line-height-20)",
        ],
        md: [
          "text-(length:--font-size-label-md)",
          "leading-(--line-height-16)",
        ],
        sm: [
          "text-(length:--font-size-label-sm)",
          "leading-(--line-height-16)",
        ],
        xs: [
          "text-(length:--font-size-label-xs)",
          "leading-(--line-height-16)",
        ],
      },
      tone: toneVariants,
      align: alignVariants,
      truncate: truncateVariants,
    },
    defaultVariants: {
      size: "md",
      tone: "default",
    },
  },
);

type LabelElement = "span" | "label" | "p";

export interface LabelProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className">,
    VariantProps<typeof labelVariants> {
  /** Override the default HTML element (defaults to span) */
  as?: LabelElement;
  /** Associates the label with a form control (only applicable when as="label") */
  htmlFor?: string;
}

/**
 * Label component for UI labels, form field labels, tags, and captions.
 * Uses Inter font family with semibold weight.
 *
 * Sizes: large (14px), medium (13px), small (12px), extra-small (11px).
 */
function Label({
  size = "md",
  tone,
  align,
  truncate,
  as = "span",
  ...props
}: LabelProps) {
  return React.createElement(as, {
    className: labelVariants({ size, tone, align, truncate }),
    ...props,
  });
}

export { Label };
